import * as React from 'react';
import {
    Badge,
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    makeStyles,
    shorthands,
    SearchBox,
    Text,
    tokens,
} from '@fluentui/react-components';
import { Library24Regular } from '@fluentui/react-icons';
import { IPollTemplate } from '../../services/DataService';
import { useDataService } from '../../app/DataServiceContext';
import { getQuestionTypeLabel } from '../../lib/pollLabels';
import { CardsSkeleton } from '../ui/Skeletons';
import ErrorBar from '../ui/ErrorBar';
import EmptyState from '../ui/EmptyState';

const useStyles = makeStyles({
    surface: {
        maxWidth: '620px',
        width: 'calc(100vw - 32px)',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxHeight: 'min(60vh, 520px)',
        overflowY: 'auto',
        paddingRight: '2px',
    },
    item: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '6px',
        width: '100%',
        padding: '12px 14px',
        textAlign: 'left',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusLarge,
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        cursor: 'pointer',
        transitionProperty: 'border-color, background-color, transform',
        transitionDuration: tokens.durationFaster,
        ':hover': {
            ...shorthands.borderColor(tokens.colorBrandStroke1),
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        ':active': {
            transform: 'scale(0.995)',
        },
        ':focus-visible': {
            outline: `2px solid ${tokens.colorStrokeFocus2}`,
        },
    },
    itemHead: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
    },
    choices: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
});

export interface ITemplatePickerDialogProps {
    open: boolean;
    onClose: () => void;
    onPick: (template: IPollTemplate) => void;
}

const TemplatePickerDialog: React.FC<ITemplatePickerDialogProps> = ({ open, onClose, onPick }) => {
    const styles = useStyles();
    const dataService = useDataService();
    const [templates, setTemplates] = React.useState<IPollTemplate[]>();
    const [error, setError] = React.useState<string>();
    const [loading, setLoading] = React.useState(false);
    const [query, setQuery] = React.useState('');

    React.useEffect(() => {
        if (!open) return;
        let active = true;
        setLoading(true);
        setError(undefined);
        dataService.getTemplates()
            .then((list) => { if (active) setTemplates(list.slice().sort((a, b) => a.Order - b.Order)); })
            .catch((e: unknown) => { if (active) setError((e as Error)?.message ?? 'Не удалось загрузить шаблоны.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [open, dataService]);

    const filtered = React.useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!templates) return [];
        if (!q) return templates;
        return templates.filter((t) => t.Text.toLowerCase().includes(q)
            || (t.Choices ?? []).some((c) => c.Text.toLowerCase().includes(q)));
    }, [templates, query]);

    return (
        <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
            <DialogSurface className={styles.surface}>
                <DialogBody>
                    <DialogTitle>Шаблоны вопросов</DialogTitle>
                    <DialogContent className={styles.content}>
                        <SearchBox
                            placeholder="Найти шаблон"
                            value={query}
                            onChange={(_, data) => setQuery(data.value)}
                            aria-label="Поиск шаблона"
                        />
                        <ErrorBar message={error} />
                        {loading && <CardsSkeleton count={3} />}
                        {!loading && !error && templates && templates.length === 0 && (
                            <EmptyState
                                icon={<Library24Regular />}
                                title="Шаблонов пока нет"
                                description="Создайте их в разделе «Шаблоны вопросов», чтобы добавлять типовые вопросы одним нажатием."
                            />
                        )}
                        {!loading && filtered.length > 0 && (
                            <div className={styles.list} role="list">
                                {filtered.map((template) => (
                                    <button
                                        key={template.Id}
                                        type="button"
                                        role="listitem"
                                        className={styles.item}
                                        onClick={() => onPick(template)}
                                    >
                                        <span className={styles.itemHead}>
                                            <Badge appearance="tint" color="brand" size="small" shape="rounded">
                                                {getQuestionTypeLabel(template.TypeCode)}
                                            </Badge>
                                            <Text weight="semibold">{template.Text}</Text>
                                        </span>
                                        {(template.Choices?.length ?? 0) > 0 && (
                                            <Text className={styles.choices}>
                                                {template.Choices.map((c) => c.Text).join(' · ')}
                                            </Text>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                        {!loading && templates && templates.length > 0 && filtered.length === 0 && (
                            <Text style={{ color: tokens.colorNeutralForeground3 }}>Ничего не найдено по запросу «{query}».</Text>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button appearance="secondary" onClick={onClose}>Закрыть</Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default TemplatePickerDialog;
